










































































































































































































































































































































































































































































































import { Button, Checkbox, CollapseItem, Radio, RadioGroup } from 'element-ui'
import { Component, Ref, Vue, Watch } from 'vue-property-decorator'

import AppCheckbox from '@/components/Checkbox.vue'
import MakePaymentAmountStep from '@/components/make-payment/MakePaymentAmountStep.vue'
import MakePaymentTermsStep from '@/components/make-payment/MakePaymentTermsStep.vue'
import PaymentMethodSelectorCheckout from '@/components/make-payment/PaymentMethodSelectorCheckout.vue'
import EnrollTabBankPayment from '@/components/modals/EnrollTabBankPayment.vue'
import SuccessPaymentTab from '@/components/modals/SuccessPaymentTab.vue'
import OptionAccordion from '@/components/OptionAccordion.vue'
import StepAccordionShell from '@/components/StepAccordionShell.vue'
import StepOptionRadio from '@/components/StepOptionRadio.vue'

import AmountChoice from '@/models/amount-choice'
import Booking from '@/models/booking'
import Customer from '@/models/customer'
import Passenger, { PassengerStatuses } from '@/models/passenger'
import Payment, {
  MINIMAL_PAYMENT_AMOUNT_CENTS,
  HIGH_AMOUNT_CONFIRM_THRESHOLD_CENTS,
} from '@/models/payment'
import PaymentModalSteps from '@/models/payment-modal-steps'

import getAbilitiesFor from '@/abilities/booking/abilities'
import defaultBookingThumbSail from '@/assets/background.jpg'
import googlePayMethodIcon from '@/assets/google.svg'
import linkMethodIcon from '@/assets/link.svg'
import defaultBookingThumbMed from '@/assets/medexperience-background.jpg'
import dayjs from '@/dayjs'
import EventBus from '@/event-bus'
import {
  humanizedCompanyName as resolveHumanizedCompanyName,
  toCurrency,
} from '@/globalMixin'
import notification from '@/notification'
import {
  BookingsActionTypes,
  BookingsMutationTypes,
} from '@/store/modules/bookings'
import {
  PaymentModalMutationTypes,
  PaymentModalActionTypes,
} from '@/store/modules/paymentModal'
import { StripeActionTypes } from '@/store/modules/stripe'

const PAYMENT_METHODS_WITH_COLLAPSE_BODY = new Set<string>(['card'])

type PaymentMethodSelectorCheckoutRef = Vue & {
  _isDestroyed?: boolean
  _isBeingDestroyed?: boolean
  validateBeforeProceed: () => Promise<boolean>
  submitAndConfirm: () => Promise<void>
}

function normalizeCheckoutRef(
  ref:
    | PaymentMethodSelectorCheckoutRef
    | PaymentMethodSelectorCheckoutRef[]
    | undefined,
): PaymentMethodSelectorCheckoutRef | undefined {
  if (!ref) {
    return undefined
  }
  if (!Array.isArray(ref)) {
    return ref
  }
  const liveRefs = ref.filter(
    (item) => item && !item._isDestroyed && !item._isBeingDestroyed,
  )
  return liveRefs[liveRefs.length - 1]
}

function computeMaxSelectablePassengerDeposits(
  booking: Booking,
  passengerCount: number,
): number {
  const passengers = Math.max(1, passengerCount || 0)
  if (!booking || booking.isCharter || booking.isYachtCharter) {
    return 1
  }
  const per = booking.depositPerPassengerCents
  if (!per || per <= 0) {
    return passengers
  }
  const slotsFromRemaining = Math.floor(booking.depositRemainingCents / per)
  return Math.max(1, Math.min(passengers, slotsFromRemaining))
}

@Component({
  components: {
    AppCheckbox,
    [Button.name]: Button,
    [Checkbox.name]: Checkbox,
    [CollapseItem.name]: CollapseItem,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
    MakePaymentAmountStep,
    MakePaymentTermsStep,
    StepAccordionShell,
    OptionAccordion,
    PaymentMethodSelectorCheckout,
    StepOptionRadio,
    EnrollTabBankPayment,
    SuccessTab: SuccessPaymentTab,
  },
})
export default class MakePaymentPage extends Vue {
  @Ref() readonly confirmPaymentSelectorRef!: PaymentMethodSelectorCheckoutRef

  initialSetupInProgress = true
  amountToPay = 0
  enteredPartialAmount = '' // What the user typed for partial payment, kept when switching between options
  depositsAmount = 1
  activeStepName = 'amount'
  reachedStepIndex = 0
  paymentMethod = 'card'
  paymentMethodCollapseActive = ''
  availablePaymentMethods: {
    id: string
    label: string
    iconClass: string
    iconText: string
    iconImage?: string
    iconImageClass?: string
  }[] = [
    {
      id: 'card',
      label: 'Credit Card',
      iconClass: 'sc-icon-card',
      iconText: '',
    },
  ]
  confirmCheckbox = false
  cardErrors: string[] = []
  checkoutDisplayTotal = ''
  checkoutDisplayCurrency = ''
  checkoutSessionId = ''
  checkoutSessionClientSecret = ''
  checkoutSessionPublishableKey = ''
  checkoutAmountCents = 0

  @Watch('bookingLoaded')
  onBookingLoadedChange(bookingLoaded: boolean) {
    if (bookingLoaded) {
      this.initialSetup()
    }
  }

  @Watch('maxSelectablePassengerDeposits')
  clampDepositsToRemaining(max: number) {
    if (this.depositsAmount <= max) {
      return
    }
    this.depositsAmount = max
    if (this.amountChoice === AmountChoice.Deposit) {
      this.setDepositAmountToPay()
    }
  }

  @Watch('total')
  syncCheckoutAmountFromValidTotal(newTotal: number) {
    if (this.initialSetupInProgress || this.amountInvalid || newTotal <= 0) {
      return
    }
    if (this.checkoutAmountCents === newTotal) {
      return
    }
    this.checkoutDisplayTotal = ''
    this.checkoutDisplayCurrency = ''
    this.checkoutAmountCents = newTotal
  }

  @Watch('amountInvalid')
  exitLateStepsIfAmountInvalid() {
    if (this.initialSetupInProgress || !this.amountInvalid) {
      return
    }
    if (
      this.activeStepName === 'confirmPayment' ||
      this.activeStepName === 'paymentMethod'
    ) {
      this.activeStepName = 'amount'
    }
  }

  @Watch('activeStepName')
  onActiveStepNameChange() {
    this.$nextTick(() => {
      const root = this.$el as HTMLElement
      if (!root || !root.querySelector) {
        return
      }
      const active = root.querySelector('.make-payment-page__step.is-active')
      if (!active) {
        return
      }
      active.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      })
    })
  }

  @Watch('paymentMethod', { immediate: true })
  @Watch('termsAccepted')
  onPaymentMethodOrTermsChange() {
    this.paymentMethodCollapseActive = this.paymentMethod
    if (this.paymentMethod === 'bank' && this.termsAccepted) {
      this.$store.dispatch(
        PaymentModalActionTypes.GetPaymentCode,
        this.enrollData,
      )
    }
  }

  onPaymentMethodCollapseInput(name: string) {
    if (!name) {
      return
    }
    if (name === this.paymentMethod) {
      return
    }
    if (name !== 'card') {
      this.cardErrors = []
    }
    this.paymentMethod = name
  }

  mounted() {
    EventBus.$on('on-bank-payment-open', () => {
      this.$store.dispatch(
        PaymentModalActionTypes.GetPaymentCode,
        this.enrollData,
      )
    })
    if (!this.bookingId) {
      this.$router.replace({ name: 'dashboard' })
      return
    }
    if (!this.booking) {
      this.$store.dispatch(BookingsActionTypes.Get, this.bookingId).then(() => {
        this.$store.dispatch(
          StripeActionTypes.LoadSdk,
          this.booking?.isYachtCharter,
        )
        this.initialSetup()
      })
    } else {
      this.$store.dispatch(
        StripeActionTypes.LoadSdk,
        this.booking.isYachtCharter,
      )
      this.initialSetup()
    }
  }

  beforeDestroy() {
    EventBus.$off('on-bank-payment-open')
  }

  get bookingId(): string {
    return (this.$route.query.bookingId as string) || ''
  }

  get booking(): Booking | null {
    if (!this.bookingId) {
      return null
    }
    return this.$store.getters.getBooking(this.bookingId)
  }

  get bookingLoaded(): boolean {
    return !!this.booking
  }

  get passengers(): Passenger[] {
    if (!this.booking) {
      return []
    }
    return this.$store.getters.getPassengersByIds(this.booking.passengerIds)
  }

  get passengerCount(): number {
    return this.passengers.filter(
      (p) => p.passengerStatus !== PassengerStatuses.Cancelled,
    ).length
  }

  get maxSelectablePassengerDeposits(): number {
    if (!this.booking) {
      return 1
    }
    return computeMaxSelectablePassengerDeposits(
      this.booking,
      this.passengerCount,
    )
  }

  get currentStep(): PaymentModalSteps {
    return this.$store.state.paymentModal.currentStep
  }

  get amountChoice(): AmountChoice | null {
    return this.$store.state.paymentModal.amountChoice
  }

  get paymentBeingProcessed(): boolean {
    return this.$store.state.paymentModal.paymentBeingProcessed
  }

  get termsAccepted(): boolean {
    return this.$store.getters.getTermsAccepted as boolean
  }

  get enrollTabCardInfo() {
    return this.$store.getters.getEnrollTabCardInfo
  }

  get paymentCode(): string {
    return this.$store.state.paymentModal.paymentCode
  }

  get stripeSdkLoaded(): boolean {
    return this.$store.getters.getStripeSdkLoaded
  }

  get steps() {
    return PaymentModalSteps
  }

  get termsStepCompanyName(): string {
    return resolveHumanizedCompanyName()
  }

  get paymentDepartureDateUtc(): string {
    if (!this.booking) {
      return ''
    }

    return dayjs.utc(this.booking.departAt).format('Do MMM, YYYY')
  }

  /**
   * Product image from API, or webpack-resolved default (SCSS url(var) in scoped CSS
   * does not always resolve assets — inline url with imported file avoids a broken / black box).
   */
  get bookingContextImageStyle(): Record<string, string> {
    const custom = this.booking?.productContextImageUrl
    if (custom) {
      return { backgroundImage: `url(${custom})` }
    }
    const fallback =
      process.env.VUE_APP_APPLICATION_NAME === 'med-experience'
        ? defaultBookingThumbMed
        : defaultBookingThumbSail
    return { backgroundImage: `url(${fallback})` }
  }

  get total(): number {
    return this.amountToPay
  }

  get displayPaymentTotal(): string {
    if (this.checkoutDisplayTotal) {
      return this.checkoutDisplayTotal
    }
    if (!this.booking) {
      return ''
    }
    return toCurrency(this.total, this.booking.currencyCode)
  }

  get amountInvalid(): boolean {
    return this.totalAmountInvalid || this.totalIsLessThanMin
  }

  get totalIsLessThanMin(): boolean {
    return !this.total || this.total < MINIMAL_PAYMENT_AMOUNT_CENTS
  }

  get totalAmountInvalid(): boolean {
    return !!(
      this.booking &&
      this.total > this.booking.totalCents - this.booking.paidCents
    )
  }

  get totalError(): string {
    if (!this.booking) {
      return ''
    }
    if (this.totalAmountInvalid) {
      return 'Total amount is larger than remaining amount'
    }
    if (this.totalIsLessThanMin || isNaN(this.total)) {
      return `Minimal payment is ${toCurrency(
        MINIMAL_PAYMENT_AMOUNT_CENTS,
        this.booking.currencyCode,
      )}`
    }
    return ''
  }

  get isLargeConfirmAmount(): boolean {
    return this.total >= HIGH_AMOUNT_CONFIRM_THRESHOLD_CENTS
  }

  get stepOrder(): string[] {
    return ['amount', 'terms', 'paymentMethod', 'confirmPayment']
  }

  isStepDisabled(stepName: string): boolean {
    return stepName !== this.activeStepName
  }

  get enrollData() {
    if (!this.booking) {
      return {
        currency: '',
        recipients: [],
        enquiryId: '',
        amountCents: 0,
        termsAccepted: false,
      }
    }
    return {
      currency: this.booking.currencyCode,
      recipients: [],
      enquiryId: this.booking.id,
      amountCents: this.total,
      termsAccepted: this.termsAccepted,
    }
  }

  get newPayment(): Payment {
    const paymentId = this.$store.getters.getCompletedPaymentId
    return this.$store.getters.getPaymentById(paymentId)
  }

  get stepCompleted(): Record<string, boolean> {
    const t = this.total
    const hasAmount = t > 0 && !this.amountInvalid
    return {
      amount: hasAmount && this.reachedStepIndex >= 1,
      terms: this.termsAccepted,
      paymentMethod: this.reachedStepIndex >= 3,
      confirmPayment: this.confirmCheckbox,
    }
  }

  get amountAllowsLaterSteps(): boolean {
    return !this.amountInvalid && this.total > 0
  }

  /**
   * Synchronous gate for navigating to a step (used by expandStep and Edit buttons).
   * Does not include async checks (e.g. card validation for confirm).
   */
  isStepAvailable(stepName: string): boolean {
    if (!this.booking) {
      return false
    }
    if (stepName === 'amount') {
      return true
    }
    if (!this.amountAllowsLaterSteps) {
      return false
    }
    if (stepName === 'terms') {
      return true
    }
    if (
      this.booking.termsTemplate &&
      !this.termsAccepted &&
      (stepName === 'paymentMethod' || stepName === 'confirmPayment')
    ) {
      return false
    }
    if (stepName === 'paymentMethod' || stepName === 'confirmPayment') {
      return true
    }
    return false
  }

  get resolvedPaymentMethodSelectorRef():
    | PaymentMethodSelectorCheckoutRef
    | undefined {
    const raw =
      this.paymentMethod === 'card'
        ? (this.$refs['paymentMethodSelectorRef-card'] as
            | PaymentMethodSelectorCheckoutRef
            | PaymentMethodSelectorCheckoutRef[]
            | undefined)
        : this.confirmPaymentSelectorRef
    return normalizeCheckoutRef(raw)
  }

  get submitDisabled(): boolean {
    return (
      this.paymentBeingProcessed ||
      !this.termsAccepted ||
      this.paymentMethod !== 'card' ||
      this.total < MINIMAL_PAYMENT_AMOUNT_CENTS ||
      this.amountInvalid ||
      this.cardErrors.length > 0
    )
  }

  get isExpressMethod(): boolean {
    return ['apple_pay', 'google_pay', 'link'].includes(this.paymentMethod)
  }

  get currentCustomerEmail(): string {
    const c = this.$store.getters.getCurrentCustomer as Customer | null
    return (c?.email && c.email.trim()) || ''
  }

  isPaymentMethodCollapseBodyEmpty(methodId: string): boolean {
    return !PAYMENT_METHODS_WITH_COLLAPSE_BODY.has(methodId)
  }

  /** Large-amount flow: checkbox must be checked before paying or using wallets */
  get confirmPaymentUiLocked(): boolean {
    return this.isLargeConfirmAmount && !this.confirmCheckbox
  }

  initialSetup() {
    const booking = this.booking
    if (!booking) {
      return
    }
    this.initialSetupInProgress = true
    this.$store.commit(
      PaymentModalMutationTypes.SetAmountChoice,
      AmountChoice.Partial,
    )
    this.amountToPay = 0
    this.enteredPartialAmount = ''
    this.depositsAmount = 1
    this.reachedStepIndex = 0
    this.activeStepName = 'amount'
    this.checkoutAmountCents = 0
    this.checkoutSessionId = ''
    this.checkoutSessionClientSecret = ''
    this.checkoutSessionPublishableKey = ''
    this.checkIfAllowedToPay(booking)
    this.$store.commit(
      PaymentModalMutationTypes.SetStep,
      PaymentModalSteps.Amount,
    )
    this.syncAmountFromStore(booking)
    this.initialSetupInProgress = false
  }

  syncAmountFromStore(booking: Booking) {
    if (this.amountChoice === AmountChoice.Remaining) {
      this.amountToPay = booking.totalCents - booking.paidCents
    } else if (this.amountChoice === AmountChoice.Deposit) {
      if (booking.isCharter || booking.isYachtCharter) {
        this.amountToPay = booking.depositRemainingCents
      } else {
        this.amountToPay =
          booking.depositPerPassengerCents * this.depositsAmount
      }
    }
  }

  checkIfAllowedToPay(booking: Booking) {
    const ability = getAbilitiesFor(booking)
    if (!ability.can('pay', 'Booking')) {
      this.$router.push({ name: 'booking', params: { id: booking.id } })
    }
  }

  async canProceedToConfirmStep(): Promise<boolean> {
    if (!this.isStepAvailable('confirmPayment')) {
      if (!this.amountAllowsLaterSteps) {
        this.activeStepName = 'amount'
      } else if (this.booking?.termsTemplate && !this.termsAccepted) {
        this.activeStepName = 'terms'
      }
      return false
    }
    if (this.paymentMethod === 'card') {
      const selectorRef = this.resolvedPaymentMethodSelectorRef
      if (!selectorRef) {
        this.onCardError(['Card form is not ready yet. Please try again.'])
        this.activeStepName = 'paymentMethod'
        return false
      }
      const valid = await selectorRef.validateBeforeProceed()
      if (!valid) {
        if (!this.cardErrors.length) {
          this.onCardError([
            'Card details could not be validated. Please check your card details and try again.',
          ])
        }
        this.activeStepName = 'paymentMethod'
        return false
      }
    }
    return true
  }

  async expandStep(name: string) {
    if (!this.isStepAvailable(name)) {
      if (name !== 'amount' && !this.amountAllowsLaterSteps) {
        this.activeStepName = 'amount'
      } else if (
        (name === 'paymentMethod' || name === 'confirmPayment') &&
        this.booking?.termsTemplate &&
        !this.termsAccepted
      ) {
        this.activeStepName = 'terms'
      }
      return
    }
    if (name === 'confirmPayment') {
      if (!(await this.canProceedToConfirmStep())) {
        return
      }
      this.reachedStepIndex = Math.max(this.reachedStepIndex, 3)
      this.activeStepName = 'confirmPayment'
      return
    }
    this.activeStepName = name
  }

  goBackToStep1() {
    this.expandStep('amount')
  }

  onAmountNext() {
    if (this.amountInvalid) {
      if (this.amountChoice === AmountChoice.Partial && this.totalError) {
        notification(this.totalError, 'error')
      }
      return
    }
    this.reachedStepIndex = Math.max(this.reachedStepIndex, 1)
    this.activeStepName = 'terms'
  }

  onTermsAcceptedChange(accepted: boolean) {
    this.$store.commit(PaymentModalMutationTypes.UpdateTermsAccepted, accepted)
  }

  onTermsAccept() {
    if (!this.amountAllowsLaterSteps) {
      this.activeStepName = 'amount'
      return
    }
    this.$store.commit(PaymentModalMutationTypes.UpdateTermsAccepted, true)
    this.reachedStepIndex = Math.max(this.reachedStepIndex, 2)
    this.activeStepName = 'paymentMethod'
  }

  async onPaymentMethodNext() {
    if (!(await this.canProceedToConfirmStep())) {
      return
    }
    this.reachedStepIndex = Math.max(this.reachedStepIndex, 3)
    this.activeStepName = 'confirmPayment'
  }

  handleAmountToPayUpdate(amount: string | number) {
    const value =
      typeof amount === 'string' ? parseFloat(amount || '0') : amount
    this.amountToPay = Math.round(value * 100)
  }

  handleEnteredPartialAmountUpdate(raw: string) {
    this.enteredPartialAmount = raw
    this.syncAmountToPayFromPartialEntry()
  }

  syncAmountToPayFromPartialEntry() {
    if (this.amountChoice !== AmountChoice.Partial) {
      return
    }
    const normalized = (this.enteredPartialAmount || '').replace(',', '.')
    const value = parseFloat(normalized || '0')
    const scaled = value * 100
    this.amountToPay = Math.round(value === value ? scaled : 0)
  }

  handleAmountChoiceUpdate(choice: AmountChoice) {
    const previous = this.amountChoice
    this.$store.commit(PaymentModalMutationTypes.SetAmountChoice, choice)
    if (previous === AmountChoice.Deposit && choice !== AmountChoice.Deposit) {
      this.amountToPay = 0
    }
    if (choice === AmountChoice.Deposit) {
      this.setDepositAmountToPay()
    } else if (choice === AmountChoice.Remaining) {
      this.setRemainingAmountToPay()
    } else {
      this.syncAmountToPayFromPartialEntry()
    }
  }

  setRemainingAmountToPay() {
    if (!this.booking) {
      return
    }
    this.amountToPay = this.booking.totalCents - this.booking.paidCents
  }

  handleDepositsAmountUpdate(depositsAmount: number) {
    const max = this.maxSelectablePassengerDeposits
    this.depositsAmount = Math.min(Math.max(depositsAmount || 1, 1), max)
    this.setDepositAmountToPay()
  }

  setDepositAmountToPay() {
    const { booking } = this
    if (!booking) {
      return
    }
    if (booking.isCharter || booking.isYachtCharter) {
      this.amountToPay = booking.depositRemainingCents
    } else {
      this.amountToPay = booking.depositPerPassengerCents * this.depositsAmount
    }
  }

  onCardError(errors: string[]) {
    this.cardErrors = errors || []
    if (
      this.cardErrors.length > 0 &&
      this.activeStepName === 'confirmPayment'
    ) {
      this.activeStepName = 'paymentMethod'
    }
  }

  onCheckoutSessionChange(payload: { totalAmount: string; currency: string }) {
    this.checkoutDisplayTotal = payload.totalAmount || ''
    this.checkoutDisplayCurrency = payload.currency || ''
  }

  onOutstandingUpdate(payload: { outstandingCents: number }) {
    if (!this.booking || typeof payload?.outstandingCents !== 'number') {
      return
    }
    this.$store.commit(BookingsMutationTypes.UpdatePaidCents, {
      bookingId: this.booking.id,
      outstandingCents: payload.outstandingCents,
    })
  }

  onCheckoutSessionCreated(payload: {
    checkoutSessionId: string
    clientSecret: string
    publishableKey: string
  }) {
    this.checkoutSessionId = payload.checkoutSessionId || ''
    this.checkoutSessionClientSecret = payload.clientSecret || ''
    this.checkoutSessionPublishableKey = payload.publishableKey || ''
  }

  onAvailableMethods(methods: { id: string; label: string }[]) {
    const normalized = methods.map((method) => {
      if (method.id === 'card') {
        return {
          id: method.id,
          label: 'Credit Card',
          iconClass: 'sc-icon-card',
          iconText: '',
        }
      }
      if (method.id === 'apple_pay') {
        return {
          id: method.id,
          label: 'Apple Pay',
          iconClass: 'sc-icon-apple',
          iconText: '',
        }
      }
      if (method.id === 'google_pay') {
        return {
          id: method.id,
          label: 'Google Pay',
          iconClass: '',
          iconText: '',
          iconImage: googlePayMethodIcon,
        }
      }
      if (method.id === 'link') {
        return {
          id: method.id,
          label: 'Link',
          iconClass: '',
          iconText: '',
          iconImage: linkMethodIcon,
          iconImageClass: 'make-payment-page__method-item-icon-img--link',
        }
      }
      return {
        id: method.id,
        label: method.label,
        iconClass: '',
        iconText: '',
      }
    })
    this.availablePaymentMethods = normalized
    const expressMethodIds = ['apple_pay', 'google_pay', 'link']
    if (
      this.paymentMethod !== 'bank' &&
      !normalized.some((method) => method.id === this.paymentMethod)
    ) {
      const expressProbePending =
        expressMethodIds.includes(this.paymentMethod) &&
        !normalized.some((method) => expressMethodIds.includes(method.id))
      if (!expressProbePending) {
        this.paymentMethod = normalized[0]?.id || 'bank'
      }
    }
  }

  onCheckoutComplete(payload: { checkoutSessionId: string }) {
    if (!payload?.checkoutSessionId) {
      return
    }
    this.handleCheckoutEnroll(payload.checkoutSessionId)
  }

  async handlePay() {
    if (this.confirmPaymentUiLocked) {
      return
    }
    if (this.paymentMethod !== 'card') {
      return
    }
    const ref = this.resolvedPaymentMethodSelectorRef
    if (!ref) {
      return
    }
    await ref.submitAndConfirm()
  }

  async handleCheckoutEnroll(checkoutSessionId: string) {
    if (!this.booking || !this.termsAccepted) {
      return
    }
    const enrollForm = {
      ...this.enrollTabCardInfo,
      ...this.enrollData,
      stripeCheckoutSessionId: checkoutSessionId,
      saveCard: false,
    }
    this.$store.commit(PaymentModalMutationTypes.EnrollStart)
    await this.$store.dispatch(PaymentModalActionTypes.Enroll, enrollForm)
    if (this.currentStep !== this.steps.Success) {
      this.activeStepName = 'paymentMethod'
    }
    this.$store.dispatch(BookingsActionTypes.Get, this.booking.id)
  }

  clearPaymentModal() {
    this.$store.commit(PaymentModalMutationTypes.ClearForm)
    this.$router.push({ name: 'booking', params: { id: this.bookingId } })
  }

  onClose() {
    this.clearPaymentModal()
  }
}
